import { useEffect, useMemo, useState } from 'react';
import type { FormEvent } from 'react';
import type {
  OrgRoutingPolicy,
  UpsertOrgRoutingPolicyRequest,
} from '@nadoutong/contracts';
import { OpsApiError } from '../api/client.ts';
import {
  useDeleteOrgRoutingPolicy,
  useOrgRoutingPolicy,
  useUpsertOrgRoutingPolicy,
} from '../api/routingPolicy.ts';
import { useToast, coerceErrorMessage } from './Toast.tsx';
import { useT } from '../i18n/index.ts';
import { INPUT_CLASS } from '../styles/forms.ts';

/**
 * Per-org routing policy editor (Epic #109 / P2.9).
 *
 * Shown on AccountDetail when the account belongs to an org. The card
 * is org-scoped (not account-scoped) — every account in the same org
 * sees the same policy. Shows a banner callout to that effect so ops
 * doesn't think they're editing the tenant-level preference.
 *
 * Unset fields: the schema intentionally distinguishes "not set"
 * (NULL) from "0". An unset `min_availability_pct` is treated as 0 by
 * the selector; an unset `max_p95_latency_ms` disables the threshold
 * entirely. We preserve that distinction on the form by letting the
 * operator clear the field to `null` via an explicit "—" toggle (for
 * weights + maxP95) and an inline clear button for the lists.
 *
 * Partial updates: PUT treats undefined fields as "leave unchanged",
 * null as "clear". We send the full form so the operator's Save is
 * always a deterministic snapshot of what they see.
 */

interface Props {
  readonly orgId: string;
  readonly orgName: string | null;
}

interface Draft {
  allowlistRaw: string;
  denylistRaw: string;
  slaWeightRaw: string;
  costWeightRaw: string;
  minAvailabilityPctRaw: string;
  maxP95LatencyMsRaw: string;
}

function draftFromPolicy(policy: OrgRoutingPolicy | null): Draft {
  if (policy === null) {
    return {
      allowlistRaw: '',
      denylistRaw: '',
      slaWeightRaw: '',
      costWeightRaw: '',
      minAvailabilityPctRaw: '',
      maxP95LatencyMsRaw: '',
    };
  }
  return {
    allowlistRaw: policy.allowlist === null ? '' : policy.allowlist.join(', '),
    denylistRaw: policy.denylist === null ? '' : policy.denylist.join(', '),
    slaWeightRaw: policy.slaWeight === null ? '' : String(policy.slaWeight),
    costWeightRaw: policy.costWeight === null ? '' : String(policy.costWeight),
    minAvailabilityPctRaw: String(policy.minAvailabilityPct),
    maxP95LatencyMsRaw:
      policy.maxP95LatencyMs === null ? '' : String(policy.maxP95LatencyMs),
  };
}

function splitList(raw: string): string[] {
  return raw
    .split(',')
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
}

export function OrgRoutingPolicyCard({ orgId, orgName }: Props): JSX.Element {
  const t = useT();
  const toast = useToast();
  const query = useOrgRoutingPolicy(orgId);
  const upsert = useUpsertOrgRoutingPolicy(orgId);
  const deleteMutation = useDeleteOrgRoutingPolicy(orgId);

  const currentPolicy = query.data?.policy ?? null;

  const [draft, setDraft] = useState<Draft>(() => draftFromPolicy(currentPolicy));
  const [formError, setFormError] = useState<string | null>(null);

  // Re-seed the form whenever the server-side policy changes (e.g. a
  // peer ops session edited it in parallel and React Query refetched).
  // Tracks the server-side identity through `updatedAt` + the mutation
  // completion; operator's in-flight edits aren't clobbered because the
  // effect only fires on refresh / initial load, not on every render.
  const serverSignature = useMemo(() => {
    if (currentPolicy === null) return 'NONE';
    return currentPolicy.updatedAt;
  }, [currentPolicy]);

  useEffect(() => {
    setDraft(draftFromPolicy(currentPolicy));
    setFormError(null);
    // We intentionally depend on `serverSignature` (not the object) so
    // references changing for no reason don't force-discard a live edit.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [serverSignature]);

  async function handleSubmit(e: FormEvent<HTMLFormElement>): Promise<void> {
    e.preventDefault();
    setFormError(null);

    const allowlist = splitList(draft.allowlistRaw);
    const denylist = splitList(draft.denylistRaw);

    let slaWeight: number | null;
    if (draft.slaWeightRaw.trim().length === 0) {
      slaWeight = null;
    } else {
      const n = Number(draft.slaWeightRaw);
      if (!Number.isFinite(n) || n < 0 || n > 1) {
        setFormError(t('routing_policy.error.weight_range'));
        return;
      }
      slaWeight = n;
    }

    let costWeight: number | null;
    if (draft.costWeightRaw.trim().length === 0) {
      costWeight = null;
    } else {
      const n = Number(draft.costWeightRaw);
      if (!Number.isFinite(n) || n < 0 || n > 1) {
        setFormError(t('routing_policy.error.weight_range'));
        return;
      }
      costWeight = n;
    }

    let minAvailabilityPct: number;
    if (draft.minAvailabilityPctRaw.trim().length === 0) {
      minAvailabilityPct = 0;
    } else {
      const n = Number(draft.minAvailabilityPctRaw);
      if (!Number.isFinite(n) || n < 0 || n > 100) {
        setFormError(t('routing_policy.error.pct_range'));
        return;
      }
      minAvailabilityPct = n;
    }

    let maxP95LatencyMs: number | null;
    if (draft.maxP95LatencyMsRaw.trim().length === 0) {
      maxP95LatencyMs = null;
    } else {
      const n = Number(draft.maxP95LatencyMsRaw);
      if (!Number.isFinite(n) || !Number.isInteger(n) || n < 0) {
        setFormError(t('routing_policy.error.latency_integer'));
        return;
      }
      maxP95LatencyMs = n;
    }

    const body: UpsertOrgRoutingPolicyRequest = {
      allowlist: allowlist.length === 0 ? null : allowlist,
      denylist: denylist.length === 0 ? null : denylist,
      slaWeight,
      costWeight,
      minAvailabilityPct,
      maxP95LatencyMs,
    };

    try {
      await upsert.mutateAsync(body);
      toast.success(t('routing_policy.toast.saved', { org: orgName ?? orgId }));
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setFormError(message);
      toast.error(message);
    }
  }

  async function handleClear(): Promise<void> {
    if (currentPolicy === null) return;
    setFormError(null);
    try {
      await deleteMutation.mutateAsync();
      toast.success(t('routing_policy.toast.cleared', { org: orgName ?? orgId }));
    } catch (err) {
      const message =
        err instanceof OpsApiError
          ? `${err.code}: ${err.message}`
          : coerceErrorMessage(err);
      setFormError(message);
      toast.error(message);
    }
  }

  const busy = upsert.isPending || deleteMutation.isPending;

  return (
    <article
      data-testid="org-routing-policy-card"
      className="flex flex-col gap-6 rounded-xl bg-surface-container p-8"
    >
      <header className="flex items-center justify-between">
        <h3 className="text-[11px] font-bold uppercase tracking-[0.1em] text-on-surface-variant">
          {t('routing_policy.title')}
        </h3>
        {query.isFetching || busy ? (
          <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline">
            {t('common.saving')}
          </span>
        ) : null}
      </header>

      <p
        className="rounded-lg bg-surface-container-low px-4 py-3 text-[12px] text-on-surface-variant"
        data-testid="org-routing-policy-body"
      >
        {t('routing_policy.body', { org: orgName ?? orgId })}
      </p>

      {query.isError ? (
        <p
          className="text-xs text-error"
          role="alert"
          data-testid="org-routing-policy-load-error"
        >
          {query.error instanceof OpsApiError
            ? `${query.error.code}: ${query.error.message}`
            : t('common.network_error')}
        </p>
      ) : null}

      {!query.isPending ? (
        <form className="flex flex-col gap-4" onSubmit={handleSubmit} noValidate>
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.allowlist')}
              </span>
              <input
                type="text"
                value={draft.allowlistRaw}
                onChange={(e) => setDraft({ ...draft, allowlistRaw: e.currentTarget.value })}
                placeholder={t('routing_policy.field.allowlist.placeholder')}
                data-testid="org-routing-policy-allowlist"
                className={INPUT_CLASS}
                disabled={busy}
              />
              <span className="text-[11px] text-outline">
                {t('routing_policy.field.allowlist.hint')}
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.denylist')}
              </span>
              <input
                type="text"
                value={draft.denylistRaw}
                onChange={(e) => setDraft({ ...draft, denylistRaw: e.currentTarget.value })}
                placeholder={t('routing_policy.field.denylist.placeholder')}
                data-testid="org-routing-policy-denylist"
                className={INPUT_CLASS}
                disabled={busy}
              />
              <span className="text-[11px] text-outline">
                {t('routing_policy.field.denylist.hint')}
              </span>
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.sla_weight')}
              </span>
              <input
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={draft.slaWeightRaw}
                onChange={(e) => setDraft({ ...draft, slaWeightRaw: e.currentTarget.value })}
                placeholder={t('routing_policy.field.weight.placeholder')}
                data-testid="org-routing-policy-sla-weight"
                className={INPUT_CLASS}
                disabled={busy}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.cost_weight')}
              </span>
              <input
                type="number"
                min={0}
                max={1}
                step="0.01"
                value={draft.costWeightRaw}
                onChange={(e) => setDraft({ ...draft, costWeightRaw: e.currentTarget.value })}
                placeholder={t('routing_policy.field.weight.placeholder')}
                data-testid="org-routing-policy-cost-weight"
                className={INPUT_CLASS}
                disabled={busy}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.min_availability_pct')}
              </span>
              <input
                type="number"
                min={0}
                max={100}
                step="0.1"
                value={draft.minAvailabilityPctRaw}
                onChange={(e) =>
                  setDraft({ ...draft, minAvailabilityPctRaw: e.currentTarget.value })
                }
                placeholder="0"
                data-testid="org-routing-policy-min-availability"
                className={INPUT_CLASS}
                disabled={busy}
              />
            </label>
            <label className="flex flex-col gap-1">
              <span className="text-[10px] font-bold uppercase tracking-[0.1em] text-outline-variant">
                {t('routing_policy.field.max_p95_latency_ms')}
              </span>
              <input
                type="number"
                min={0}
                step="1"
                value={draft.maxP95LatencyMsRaw}
                onChange={(e) =>
                  setDraft({ ...draft, maxP95LatencyMsRaw: e.currentTarget.value })
                }
                placeholder={t('routing_policy.field.max_p95.placeholder')}
                data-testid="org-routing-policy-max-p95"
                className={INPUT_CLASS}
                disabled={busy}
              />
            </label>
          </div>
          {formError !== null ? (
            <p
              role="alert"
              className="text-xs text-error"
              data-testid="org-routing-policy-error"
            >
              {formError}
            </p>
          ) : null}
          {currentPolicy !== null ? (
            <p className="text-[11px] text-outline" data-testid="org-routing-policy-updated-at">
              {t('routing_policy.updated_at', { iso: currentPolicy.updatedAt })}
            </p>
          ) : (
            <p className="text-[11px] text-outline" data-testid="org-routing-policy-empty">
              {t('routing_policy.empty')}
            </p>
          )}
          <div className="flex items-center justify-end gap-3">
            <button
              type="button"
              disabled={busy || currentPolicy === null}
              onClick={() => void handleClear()}
              data-testid="org-routing-policy-clear"
              className="inline-flex items-center justify-center rounded-lg bg-surface-container-highest px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-surface transition-colors hover:bg-surface-bright focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {deleteMutation.isPending
                ? t('routing_policy.clear.pending')
                : t('routing_policy.clear')}
            </button>
            <button
              type="submit"
              disabled={busy}
              data-testid="org-routing-policy-submit"
              className="inline-flex items-center justify-center rounded-lg bg-primary-container px-4 py-2 text-xs font-bold uppercase tracking-[0.08em] text-on-primary transition-all hover:shadow-[0_0_16px_rgba(173,198,255,0.2)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary focus-visible:ring-offset-2 focus-visible:ring-offset-surface-container disabled:opacity-40 disabled:pointer-events-none"
            >
              {upsert.isPending ? t('common.saving') : t('routing_policy.save')}
            </button>
          </div>
        </form>
      ) : null}
    </article>
  );
}
